import { Platform } from 'react-native';

type TVRemoteEvent = { eventType: string; eventKeyAction?: number };

// On TV, react-native exposes useTVEventHandler. Off TV (web/mobile) it's a no-op.
// Platform.isTV is constant for the app lifetime, so this conditional hook
// selection is safe (same pattern as EventHandlingDemo).
const useTVEventHandler = Platform.isTV
  ? require('react-native').useTVEventHandler
  : (_handler: (event: TVRemoteEvent) => void) => {};

// Android KeyEvent.ACTION_DOWN. Android emits each transport key on both edges
// (down=0, up=1); we act on the UP edge only and ignore DOWN. UP is the most
// reliable single edge: a release always generates one ACTION_UP, it never
// repeats while the key is held, and on our Android TV the active expo-video
// MediaSession swallows the DOWN edge anyway. iOS sends no eventKeyAction, so
// `=== ACTION_DOWN` is false there and the event fires once.
const ACTION_DOWN = 0;

type Handlers = {
  onRewind?: () => void;
  onPlayPause?: () => void;
  onFastForward?: () => void;
};

/**
 * Maps the TV remote's hardware transport buttons to callbacks.
 *
 * Platform support: Android TV surfaces all three (rewind / playPause /
 * fastForward). Apple TV's Siri remote only has playPause — rewind/fastForward
 * are not available there.
 */
export function useTVTransportControls({
  onRewind,
  onPlayPause,
  onFastForward,
}: Handlers) {
  useTVEventHandler((event: TVRemoteEvent) => {
    console.log(
      `[useTVTransportControls] Received TV remote event: type=${event.eventType}, action=${event.eventKeyAction}`,
    );
    if (event.eventKeyAction === ACTION_DOWN) {
      console.log(
        `[useTVTransportControls] Ignoring key event type=${event.eventType}.`,
      );
      return;
    }
    switch (event.eventType) {
      case 'rewind':
        onRewind?.();
        break;
      case 'playPause':
        onPlayPause?.();
        break;
      case 'fastForward':
        onFastForward?.();
        break;
    }
  });
}
